import { useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Check, ChefHat, Gift, Mail, Milk, Plus, ShoppingBag, Timer } from "lucide-react";
import { toast } from "sonner";
import { MaskedLines, Reveal } from "@/components/Reveal";
import { Marquee } from "@/components/Marquee";
import { ChefWillBadge } from "@/components/ChefWillBadge";
import { useQuote } from "@/components/QuoteDialog";
import { useCart } from "@/context/CartContext";
import { useCatalog } from "@/hooks/useCatalog";
import { gbp } from "@/lib/api";

const ICONS = { "Just add milk": Milk, "Ready fast": Timer, "Chef crafted": ChefHat, "Gift ready": Gift };
const PINK = "#D0466F";

const FjHero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y1 = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const y2 = useTransform(scrollYProgress, [0, 1], ["0%", "-18%"]);
  return (
    <section ref={ref} className="fj-dots relative overflow-hidden pb-20 pt-32 text-[#3B1220] sm:pt-40" data-testid="flapjack-hero">
      <div className="relative mx-auto grid max-w-[1400px] items-center gap-12 px-5 sm:px-8 lg:grid-cols-12">
        <div className="lg:col-span-6">
          <motion.span initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="inline-block rounded-full bg-[#D0466F] px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-white">
            Classic American style
          </motion.span>
          <motion.img
            src="/images/flapjack_logo.png"
            alt="Flapjack"
            initial={{ scale: 0.55, rotate: -8, opacity: 0 }}
            animate={{ scale: 1, rotate: 0, opacity: 1 }}
            transition={{ type: "spring", stiffness: 120, damping: 11, delay: 0.1 }}
            className="mt-6 w-full max-w-[560px]"
            data-testid="flapjack-heading"
          />
          <MaskedLines onMount delay={0.45} lines={["Tall, fluffy stacks.", "Crisp, golden waffles."]} className="font-retro mt-6 text-3xl leading-[1.15] text-[#D0466F] sm:text-5xl" />
          <Reveal delay={0.7}>
            <p className="mt-6 max-w-lg text-lg text-[#5A2A3A]">Chef-crafted, pre-measured mixes from Culifect Catering. Just add milk, whisk and cook. Diner-style breakfasts at home in minutes.</p>
            <a href="#range" className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#D0466F] px-7 py-4 font-semibold text-white transition-transform hover:-translate-y-0.5" data-testid="flapjack-shop-anchor">
              <ShoppingBag className="h-4 w-4" /> Shop the mixes
            </a>
          </Reveal>
        </div>
        <div className="relative h-[460px] sm:h-[580px] lg:col-span-6">
          <motion.div initial={{ scale: 0.4, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }} className="absolute left-1/2 top-1/2 h-[88%] aspect-square -translate-x-1/2 -translate-y-1/2 rounded-full bg-[radial-gradient(circle,#fff_0%,#FFC2D3_55%,transparent_72%)]" />
          <motion.div style={{ y: y2 }} className="absolute right-0 top-0 h-[78%] sm:right-4">
            <motion.img src="/images/fj_waffle_pouch.webp" alt="Flapjack Classic American Waffle Mix pouch" initial={{ y: 80, opacity: 0, rotate: 18 }} animate={{ y: 0, opacity: 1, rotate: 8 }} transition={{ type: "spring", stiffness: 70, damping: 14, delay: 0.55 }} className="h-full w-auto object-contain drop-shadow-[0_30px_40px_rgba(59,18,32,0.35)]" />
          </motion.div>
          <motion.div style={{ y: y1 }} className="absolute bottom-0 left-0 h-[86%] sm:left-6">
            <motion.img src="/images/fj_pancake_pouch.webp" alt="Flapjack Classic American Pancake Mix pouch" initial={{ y: 120, opacity: 0, rotate: -16 }} animate={{ y: 0, opacity: 1, rotate: -5 }} transition={{ type: "spring", stiffness: 70, damping: 14, delay: 0.3 }} whileHover={{ rotate: -2, scale: 1.03 }} className="h-full w-auto object-contain drop-shadow-[0_40px_50px_rgba(59,18,32,0.4)]" />
          </motion.div>
          <div className="absolute bottom-2 right-2 sm:right-8">
            <ChefWillBadge id="cw-hero" />
          </div>
        </div>
      </div>
    </section>
  );
};

const ProductFeature = ({ p, i }) => {
  const { add, setOpen } = useCart();
  const flip = i % 2 === 1;
  const onAdd = () => {
    add(p.lookup_key);
    toast.success(`${p.name} added`, { action: { label: "View basket", onClick: () => setOpen(true) } });
  };
  return (
    <div className={`grid items-center gap-12 lg:grid-cols-12 ${i ? "mt-28" : ""}`} data-testid={`product-card-${p.lookup_key}`}>
      <Reveal className={`lg:col-span-6 ${flip ? "lg:order-2" : ""}`}>
        <div className="group relative flex aspect-[5/6] items-center justify-center overflow-hidden rounded-[40px] bg-[radial-gradient(circle_at_50%_45%,#fff_0%,#FFD8E2_60%)] p-8 ring-1 ring-[#D0466F]/20 sm:aspect-[6/6]">
          <img src={p.image} alt={`${p.name} pouch`} className="h-full w-auto object-contain drop-shadow-[0_30px_40px_rgba(59,18,32,0.3)] transition-transform duration-700 group-hover:-rotate-2 group-hover:scale-[1.04]" />
        </div>
      </Reveal>
      <div className={`lg:col-span-6 ${flip ? "lg:order-1" : ""}`}>
        <p className="eyebrow text-[#B83560]">0{i + 1} — {p.short}</p>
        <MaskedLines lines={[p.tagline]} className="font-retro mt-4 text-3xl leading-[1.15] text-[#D0466F] sm:text-4xl" />
        <Reveal delay={0.1}>
          <p className="mt-5 font-semibold text-[#3B1220]">{p.intro}</p>
          <p className="mt-4 text-[#5A2A3A]">{p.description}</p>
          <div className="mt-8 grid grid-cols-2 gap-3">
            {(p.features ?? []).map((f) => {
              const Icon = ICONS[f.t] ?? Check;
              return (
                <div key={f.t} className="rounded-2xl bg-white p-4 shadow-sm transition-transform hover:-translate-y-1">
                  <div className="flex items-center gap-2">
                    <span className="grid h-8 w-8 place-items-center rounded-full bg-[#FFE1EA]"><Icon className="h-4 w-4" style={{ color: PINK }} /></span>
                    <span className="text-sm font-bold">{f.t}</span>
                  </div>
                  <p className="mt-2 text-xs leading-relaxed text-[#5A2A3A]">{f.d}</p>
                </div>
              );
            })}
          </div>
          <div className="mt-8 flex items-center gap-5">
            <span className="font-retro text-4xl text-[#3B1220]" data-testid={`product-price-${p.lookup_key}`}>{gbp(p.amount)}</span>
            <button onClick={onAdd} className="inline-flex items-center gap-2 rounded-full bg-[#D0466F] px-6 py-3.5 font-semibold text-white transition-transform hover:scale-105" data-testid={`product-add-${p.lookup_key}`}>
              <Plus className="h-4 w-4" /> Add to basket
            </button>
          </div>
        </Reveal>
      </div>
    </div>
  );
};

const HowTo = ({ products }) => {
  const [active, setActive] = useState(0);
  const p = products[active];
  if (!p) return null;
  return (
    <section className="bg-[#3B1220] py-24 text-[#FFF1F5] sm:py-32" data-testid="howto-section">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
        <div className="flex flex-col justify-between gap-6 sm:flex-row sm:items-end">
          <div>
            <p className="eyebrow text-[#FF9EBB]">Let's make {active ? "waffles" : "pancakes"}</p>
            <h2 className="font-retro mt-4 text-4xl text-[#FF9EBB] sm:text-6xl" data-testid="howto-title">{p.steps_title}</h2>
          </div>
          <div className="inline-flex rounded-full bg-white/10 p-1">
            {products.map((x, i) => (
              <button key={x.lookup_key} onClick={() => setActive(i)} className={`relative rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${active === i ? "text-white" : "text-[#FFD1DF] hover:text-white"}`} data-testid={`howto-tab-${x.lookup_key}`}>
                {active === i && <motion.span layoutId="howto-pill" className="absolute inset-0 rounded-full bg-[#D0466F]" transition={{ type: "spring", stiffness: 300, damping: 28 }} />}
                <span className="relative">{x.short}</span>
              </button>
            ))}
          </div>
        </div>
        <div className="relative mt-14 grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
          <div className="absolute left-6 right-6 top-6 hidden border-t-2 border-dashed border-[#FF9EBB]/40 lg:block" />
          {(p.steps ?? []).map((s, i) => (
            <motion.div key={`${active}-${s.t}`} initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: i * 0.08 }} className="relative">
              <span className="relative grid h-12 w-12 place-items-center rounded-full bg-[#D0466F] font-retro text-xl text-white ring-8 ring-[#3B1220]">{i + 1}</span>
              <h3 className="mt-5 text-2xl font-bold">{s.t}</h3>
              <p className="mt-2 text-[#F5D5DF]">{s.d}</p>
            </motion.div>
          ))}
        </div>
        <div className="mt-20" data-testid="serving-ideas">
          <h3 className="font-retro text-3xl text-[#FF9EBB] sm:text-4xl">Serving ideas</h3>
          <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {(p.serving ?? []).map(([k, v, img], i) => (
              <motion.div key={`${active}-${k}`} initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: i * 0.08 }} className="group overflow-hidden rounded-[28px] bg-[#FFF1F5] text-[#3B1220]" data-testid={`serving-idea-${i}`}>
                <div className="aspect-[4/3] overflow-hidden">
                  {img && <img src={img} alt={`${p.short}: ${k}`} className="h-full w-full object-cover object-[50%_70%] transition-transform duration-700 group-hover:scale-110" />}
                </div>
                <div className="p-5">
                  <p className="font-retro text-xl text-[#D0466F]">{k}</p>
                  <p className="mt-1 text-sm text-[#5A2A3A]">Top with {v}.</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
        <div className="mt-6">
          <motion.div key={`perfect-${active}`} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08 }} className="rounded-[32px] bg-[#FFD8E2] p-8 text-[#3B1220] sm:p-10" data-testid="perfect-for">
            <h3 className="font-retro text-3xl text-[#D0466F]">Perfect for</h3>
            <ul className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {(p.perfect_for ?? []).map((t) => (
                <li key={t} className="flex gap-3"><span className="mt-2 h-2 w-2 shrink-0 rounded-full bg-[#D0466F]" /><span>{t}</span></li>
              ))}
            </ul>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default function Flapjack() {
  const { products, loading, failed } = useCatalog();
  const { count, setOpen } = useCart();
  const { openQuote } = useQuote();

  return (
    <div data-testid="flapjack-page">
      <FjHero />
      <Marquee
        items={["Just add milk", "Ready fast", "Chef crafted", "Gift ready", "A recipe by Chef Will"]}
        tone="bg-[#D0466F] border-[#B83560] text-white"
        font="font-retro"
        star="text-[#FFD8E2]"
      />
      <section id="range" className="bg-[#FFF6F8] py-24 sm:py-32" data-testid="products-section">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
          <div className="mb-16 flex flex-col justify-between gap-6 sm:flex-row sm:items-end">
            <MaskedLines lines={["The range."]} className="font-retro text-5xl text-[#3B1220] sm:text-7xl" />
            <button onClick={() => setOpen(true)} className="inline-flex items-center gap-2 rounded-full bg-[#3B1220] px-6 py-3.5 font-semibold text-white transition-transform hover:-translate-y-0.5" data-testid="flapjack-open-cart-button">
              <ShoppingBag className="h-4 w-4" /> Basket ({count})
            </button>
          </div>
          {loading && <p className="text-stone-600">Loading products…</p>}
          {failed && <p className="text-stone-700" data-testid="products-error">The shop is unavailable right now. Please try again shortly.</p>}
          {products.map((p, i) => <ProductFeature key={p.lookup_key} p={p} i={i} />)}
        </div>
      </section>
      <HowTo products={products} />
      <section className="fj-dots py-20">
        <div className="mx-auto flex max-w-[1400px] flex-col items-start justify-between gap-6 px-5 sm:flex-row sm:items-center sm:px-8">
          <div>
            <p className="eyebrow text-[#B83560]">Retail & trade enquiries</p>
            <p className="font-retro mt-3 text-3xl text-[#3B1220] sm:text-4xl">Stocking a café, hamper or kitchen?</p>
            <a href="mailto:culifectcatering@gmail.com" className="mt-3 inline-flex items-center gap-2 font-semibold text-[#B83560] hover:underline" data-testid="flapjack-trade-email">
              <Mail className="h-4 w-4" /> culifectcatering@gmail.com · West Sussex, UK
            </a>
          </div>
          <button onClick={() => openQuote("corporate", "")} className="rounded-full bg-[#D0466F] px-7 py-4 font-semibold text-white transition-transform hover:-translate-y-0.5" data-testid="flapjack-trade-button">
            Send a trade enquiry
          </button>
        </div>
      </section>
    </div>
  );
}
